import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import * as XLSX from "xlsx";
import { getComponents } from "../api/componentsAPI";
import { createWorkOrder, getPaginatedWorkOrders, getWorkOrderById, updateWorkOrder } from "../api/work-orderAPI";
import { getWorkOrderPartsByWorkOrderId } from "../api/work-order-partsAPI";
import { getBillOfEquipmentWosByWorkOrderId } from "../api/bill-of-equipment-woAPI";
import { getBillOfLabourWosByWorkOrderId } from "../api/bill-of-labour-woAPI";
import { getBillOfServicesWosByWorkOrderId } from "../api/bill-of-services-woAPI";
import { createWorkOrderForm, getWorkOrderFormsByWorkOrderId } from "../api/work-order-formsAPI";
import { getFormAssignments } from "../api/form-assignmentsAPI";
import { getForms, getFormById } from "../api/formsAPI";
import { createFormData } from "../api/form-dataAPI";
import { createWorkOrderFormAction } from "../api/work-order-form-actionsAPI";
import { getPurchaseOrderLineById } from "../api/purchase-order-linesAPI";
import { getPurchaseOrderById } from "../api/purchase-orderAPI";
import { getTaskComments, createTaskComment, updateTaskComment, deleteTaskComment } from "../api/task-commentsAPI";
import { useTableFilters } from "../utils/useTableFilters";
import { useFormBuilder } from "../utils/useFormBuilder";
import { validateForm, useValidator } from "../utils/useValidator";
import { fetchData } from '../utils/apiHelpers';
import { formatIntDate, parseUtc, toUtcIsoWithTime } from "../utils/dateUtils.js";
import { showToastError } from "../utils/errorController";
import {
  buildWorkOrderCompleteFields,
  buildWorkOrderStatusFields,
  buildWorkOrderValidationFields,
  getWorkOrderStatusValidationErrors,
} from '../utils/workOrderFormHelpers';
import { useSetting } from "../utils/AppSettingsContext";
import { useUserProfile } from "../masterLayout/MasterLayoutBody";
import TaskEditModal from "./child/TaskEditModal.jsx";
import ReplaceComponentWorkOrderModal from "./child/ReplaceComponentWorkOrderModal.jsx";
import ComponentWorkOrderSelectionModal from "./child/ComponentWorkOrderSelectionModal.jsx";
import ComponentWorkOrdersModal from "./child/ComponentWorkOrdersModal.jsx";
import WorkOrderDetails from "./WorkOrderDetails";
import FormViewModal from "./FormViewModal";
import { getWorkOrderStatus } from '../api/work-order-statusAPI';
import { useModule } from '../utils/useModule';
import WorkOrderEditModal, { getInitialWorkOrder, mapTaskBillingToWorkOrderItems } from './WorkOrderEditModal';
import { getBillOfEquipmentByTaskId } from "../api/bill-of-equipmentAPI";
import { getBillOfLabourByTaskId } from "../api/bill-of-labourAPI";
import { getBillOfMaterialsByTaskId } from "../api/bill-of-materialsAPI";
import { getBillOfServicesByTaskId } from "../api/bill-of-servicesAPI";
import { getTechnicians } from "../api/techniciansAPI";
import { getTeamsById } from "../api/teamsAPI";
import { create, deleteOne, update } from "../api";
import { saveWorkOrderParts } from "./WorkOrderPartsTab";
import { getTrades } from "../api/tradesAPI";
import { getExpenseCodes } from "../api/expense-codeAPI";
import { getExternalServices } from "../api/external-servicesAPI";
import { getAssetById } from "../api/assetAPI";
import Calendar from "./child/Calendar";
import { useSearchParams } from "react-router-dom";

const ComponentManagementLayer = ({ taskType = "component", viewMode = "table" }) => {
  const [components, setComponents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarComponents, setCalendarComponents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const listContainerRef = useRef(null);
  const componentFetchRequestIdRef = useRef(0);
  const calendarFetchRequestIdRef = useRef(0);
  const hasInitialTaskTypeRunRef = useRef(false);
  const lastComponentRequestKeyRef = useRef("");
  const lastCalendarRequestKeyRef = useRef("");
  const commentFetchInProgress = useRef(new Set());
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showWorkOrderSelectionModal, setShowWorkOrderSelectionModal] = useState(false);
  const [matchingWorkOrders, setMatchingWorkOrders] = useState([]);
  const [existingWorkOrder, setExistingWorkOrder] = useState(null);
  const [replaceMode, setReplaceMode] = useState("create");
  const [checkingExistingWorkOrders, setCheckingExistingWorkOrders] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewComponent, setReviewComponent] = useState(null);
  const [taskComments, setTaskComments] = useState({});
  const [reviewCommentsLoading, setReviewCommentsLoading] = useState(false);
  const [taskOccurrences, setTaskOccurrences] = useState([]);
  const [showWorkOrderDetailsModal, setShowWorkOrderDetailsModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [workOrderDetails, setWorkOrderDetails] = useState(null);
  const [workOrderParts, setWorkOrderParts] = useState([]);
  const [workOrderLabour, setWorkOrderLabour] = useState([]);
  const [workOrderServices, setWorkOrderServices] = useState([]);
  const [workOrderEquipment, setWorkOrderEquipment] = useState([]);
  const [workOrderForms, setWorkOrderForms] = useState([]);
  const [workOrderDetailsLoading, setWorkOrderDetailsLoading] = useState(false);
  const [workOrderStatuses, setWorkOrderStatuses] = useState([]);
  const [showAddWorkOrderModal, setShowAddWorkOrderModal] = useState(false);
  const [newWorkOrder, setNewWorkOrder] = useState(getInitialWorkOrder());
  const [workOrderModalLoading, setWorkOrderModalLoading] = useState(false);
  const [workOrderEditingId, setWorkOrderEditingId] = useState(null);
  const [workOrderGeneralOnly, setWorkOrderGeneralOnly] = useState(true);
  const [workOrderAssetOption, setWorkOrderAssetOption] = useState(null);
  const [workOrderTaskOption, setWorkOrderTaskOption] = useState(null);
  const [crewOptions, setCrewOptions] = useState([]);
  const [deletedPartIds, setDeletedPartIds] = useState([]);
  const [deletedLabourIds, setDeletedLabourIds] = useState([]);
  const [deletedServiceIds, setDeletedServiceIds] = useState([]);
  const [deletedEquipmentIds, setDeletedEquipmentIds] = useState([]);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({});
  const [equipmentErrors, setEquipmentErrors] = useState({});
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [editingLabourIndex, setEditingLabourIndex] = useState(null);
  const [labourForm, setLabourForm] = useState({});
  const [labourErrors, setLabourErrors] = useState({});
  const [showFormModal, setShowFormModal] = useState(false);
  const [showFormView, setShowFormView] = useState(false);
  const [viewWorkOrderFormId, setViewWorkOrderFormId] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [formOptions, setFormOptions] = useState([]);
  const [formTargetWorkOrder, setFormTargetWorkOrder] = useState(null);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [actualPartsCost, setActualPartsCost] = useState('');
  const [actualLabourCost, setActualLabourCost] = useState('');
  const [actualMiscCost, setActualMiscCost] = useState('');
  const [actualLabourHours, setActualLabourHours] = useState('');
  const [duration, setDuration] = useState('');
  const [actualDuration, setActualDuration] = useState('');
  const durationEditedRef = useRef(false);
  const actualDurationEditedRef = useRef(false);
  const prevScheduledAtRef = useRef(null);
  const prevStartedAtRef = useRef(null);
  const scheduledDurationRef = useRef(null);
  const prevStatusIdRef = useRef(null);
  const pendingActualDurationSyncRef = useRef(false);
  const prevCompletedAtRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");
  const overdueParam = searchParams.get("overdue");
  const dataFilterTypeParam = searchParams.get("dataFilterType");
  const dataFromElementParam = searchParams.get("dataFromElement");
  const departmentParam = searchParams.get("departmentId");
  const departmentIdsParam = searchParams.get("departmentIds");
  const assetParam = searchParams.get("assetId");
  const [initialDepartmentIdsParam] = useState(departmentIdsParam);
  const clearFilterQueryParams = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);
  const isServiceTaskType = taskType === "service";
  const isInspectionTaskType = taskType === "inspection";
  const isComplianceTaskType = taskType === "compliance";
  const isServiceLikeTaskType = isServiceTaskType || isInspectionTaskType;
  const shouldShowComplianceStatus = isComplianceTaskType;
  const tableColumnCount = isServiceLikeTaskType ? 11 : shouldShowComplianceStatus ? 13 : 12;
  const userProfile = useUserProfile();
  const privileges = userProfile?.privileges || [];
  const userId = userProfile?.id ?? null;
  const canCreateTaskComments = privileges.includes("tasks:update") || privileges.includes("tasks:create");
  const canEditTaskComments = privileges.includes("tasks:update");
  const canDeleteTaskComments = privileges.includes("tasks:delete");
  const canUpdateWorkOrders = privileges.includes("work-orders:update");
  const defaultFocusThresholds = useMemo(
    () => [
      { class: "danger-focus", max: 0 },
      { class: "alert-focus", max: 5 },
      { class: "warning-focus", max: 20 },
      { class: "success-focus", max: 35 },
    ],
    [],
  );

  console.log('taskType', taskType);

  const focusThresholdsSetting = useSetting("component_life_warning", defaultFocusThresholds);
  const workOrdersEnabled = useModule('workorders');
  const showCostResp = useSetting('service_cost_resp');
  const shiftStartTime = useSetting('shift_start_time');
  const useCostResponsibility = useSetting('use_cost_responsibility');
  const defaultPartsExpenseCode = useSetting('default_expense_parts');
  const defaultLabourExpenseCode = useSetting('default_expense_labour');
  const defaultServiceExpenseCode = useSetting('default_expense_services');

  const formatOptionColor = useCallback(
    (option) => (
      <div className="d-flex align-items-center gap-2">
        <span
          className={`d-inline-block rounded-circle bg-${option.colour || option.bootstrapName || 'secondary'}`}
          style={{ width: '10px', height: '10px' }}
        ></span>
        <span>{option.label}</span>
      </div>
    ),
    [],
  );

  const focusThresholds = useMemo(() => {
    if (Array.isArray(focusThresholdsSetting)) {
      return focusThresholdsSetting;
    }
    if (focusThresholdsSetting && typeof focusThresholdsSetting === "object") {
      return Object.entries(focusThresholdsSetting)
        .map(([cls, max]) => {
          const parsedMax = typeof max === "string" ? Number.parseFloat(max) : Number(max);
          return {
            class: cls,
            max: Number.isFinite(parsedMax) ? parsedMax : 0,
          };
        })
        .sort((a, b) => a.max - b.max);
    }
    return defaultFocusThresholds;
  }, [defaultFocusThresholds, focusThresholdsSetting]);

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById("header-search-container"));
  }, []);

  useEffect(() => {
    const debounce = window.setTimeout(() => {
      const trimmedSearch = searchTerm.trim();
      setActiveSearchTerm(trimmedSearch.length >= 2 ? trimmedSearch : "");
    }, 250);

    return () => {
      window.clearTimeout(debounce);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchData(getWorkOrderStatus, {}, setWorkOrderStatuses);
  }, [])

  useEffect(() => {
    fetchData(getTrades, {}, setTradeOptions);
    fetchData(getExpenseCodes, {}, setExpenseOptions);
    fetchData(getExternalServices, {}, setServiceOptions);
  }, []);

  useEffect(() => {
    if (!newWorkOrder.teamsId) {
      getTechnicians()
        .then((technicians) => {
          const crewList = technicians || [];
          setCrewOptions(crewList.map((t) => ({ value: t.userId, label: t.users?.name ?? t.name })));
        })
        .catch((err) => {
          console.error('Error loading technicians', err);
          setCrewOptions([]);
        });
      return;
    }
    getTeamsById(newWorkOrder.teamsId)
      .then((team) => {
        const crewList = team?.technicians || [];
        setCrewOptions(crewList.map((t) => ({ value: t.userId, label: t.name })));
      })
      .catch((err) => {
        console.error('Error loading team technicians', err);
        setCrewOptions([]);
      });
  }, [newWorkOrder.teamsId]);

  useEffect(() => {
    if (!newWorkOrder.startedAt || actualDuration === '') return;
    const start = new Date(newWorkOrder.startedAt);
    const hrs = parseFloat(actualDuration);
    if (Number.isNaN(start.getTime()) || Number.isNaN(hrs)) return;
    if (!actualDurationEditedRef.current && prevStartedAtRef.current === newWorkOrder.startedAt) return;
    const end = new Date(start.getTime() + hrs * 3600000);
    const iso = end.toISOString();
    if (newWorkOrder.completedAt !== iso) {
      pendingActualDurationSyncRef.current = true;
      setNewWorkOrder((prev) => ({ ...prev, completedAt: iso }));
    }
    actualDurationEditedRef.current = false;
    prevStartedAtRef.current = newWorkOrder.startedAt;
  }, [actualDuration, newWorkOrder.startedAt]);

  const sortCommentsDesc = useCallback((comments = []) => {
    const array = Array.isArray(comments) ? comments : [];
    return [...array].sort((a, b) => {
      const getTime = (value) => {
        const raw = value?.createdAt ?? value?.created_at;
        if (!raw) return 0;
        const date = new Date(raw);
        return Number.isFinite(date.getTime()) ? date.getTime() : 0;
      };
      const timeA = getTime(a);
      const timeB = getTime(b);
      return timeB - timeA;
    });
  }, []);

  const updateTaskCommentsState = useCallback(
    (taskId, comments) => {
      if (!taskId) return;
      setTaskComments((prev) => ({
        ...prev,
        [taskId]: sortCommentsDesc(comments),
      }));
    },
    [sortCommentsDesc],
  );

  const fetchCommentsForTask = useCallback(
    async (taskId, { showLoader = false } = {}) => {
      if (!taskId) return [];
      if (showLoader) {
        setReviewCommentsLoading(true);
      }
      try {
        const response = await getTaskComments(taskId);
        const comments = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        updateTaskCommentsState(taskId, comments);
        return comments;
      } catch (error) {
        console.error("Error fetching task comments:", error);
        updateTaskCommentsState(taskId, []);
        return [];
      } finally {
        if (showLoader) {
          setReviewCommentsLoading(false);
        }
      }
    },
    [updateTaskCommentsState],
  );

  const handleCreateTaskComment = useCallback(
    async (taskId, commentText) => {
      if (!taskId) return;
      const payload = {
        comment: commentText,
        createdBy: userId,
      };
      await createTaskComment(taskId, payload);
      await fetchCommentsForTask(taskId, { showLoader: true });
    },
    [fetchCommentsForTask, userId],
  );

  const handleUpdateTaskComment = useCallback(
    async (taskId, commentId, commentText) => {
      if (!taskId || !commentId) return;
      const payload = {
        comment: commentText,
        updatedBy: userId,
      };
      await updateTaskComment(taskId, commentId, payload);
      await fetchCommentsForTask(taskId, { showLoader: true });
    },
    [fetchCommentsForTask, userId],
  );

  const handleDeleteTaskComment = useCallback(
    async (taskId, commentId) => {
      if (!taskId || !commentId) return;
      await deleteTaskComment(taskId, commentId);
      await fetchCommentsForTask(taskId, { showLoader: true });
    },
    [fetchCommentsForTask],
  );

  const buildTaskOccurrencesData = useCallback((task) => {
    if (!task) return;

    const occurrences = Array.isArray(task?.usageOccurences)
      ? task.usageOccurences
      : Array.isArray(task?.usageOccurrences)
        ? task.usageOccurrences
        : [];

    const measurementUnits = task?.calendarBased === 1
      ? "Days"
      : task?.assetMeter?.meter?.measurementUnits?.unitName || "Days";
    const occurrenceTitle = task?.schedulingType === "cycle"
      ? `${task?.cycleGroupName}` : `${[task?.code?.code, task?.name].filter(Boolean).join(" - ")} Occurrences`;
    setTaskOccurrences([
      occurrences,
      {
        measurementUnits,
        title: occurrenceTitle || "Task Occurrences",
      },
      task
    ]);
  }, []);

  const handleReviewComponent = useCallback(
    (task) => {
      if (!task) return;
      const workOrders = (task.workOrders ?? []).filter(
        (wo) => ![1, 2].includes(wo.statusId),
      );
      setReviewComponent({ ...task, workOrders });
      buildTaskOccurrencesData(task);
      if (task?.id) {
        fetchCommentsForTask(task.id, { showLoader: true });
      }
      setShowReviewModal(true);
    },
    [buildTaskOccurrencesData, fetchCommentsForTask],
  );

  const resolveWorkOrderId = useCallback((workOrder) => {
    if (!workOrder) return null;
    const rawId = typeof workOrder === "object" ? (workOrder.id ?? workOrder.workOrderId) : workOrder;
    const parsed = Number(rawId);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const loadWorkOrderRelatedData = useCallback(async (workOrderId) => {
    if (!workOrderId) return;
    const [parts, equipment, labour, services, forms] = await Promise.all([
      getWorkOrderPartsByWorkOrderId(workOrderId),
      getBillOfEquipmentWosByWorkOrderId(workOrderId),
      getBillOfLabourWosByWorkOrderId(workOrderId),
      getBillOfServicesWosByWorkOrderId(workOrderId),
      getWorkOrderFormsByWorkOrderId(workOrderId),
    ]);
    const partsData = Array.isArray(parts) ? parts : [];
    const servicesData = Array.isArray(services) ? services : [];
    const partsWithSource = await Promise.all(
      partsData.map(async (part) => {
        if (part?.poNumber) {
          return part;
        }
        if (part?.source === "PURCHASE ORDER" && part?.poLineId) {
          try {
            const line = await getPurchaseOrderLineById(part.poLineId);
            const po = line?.purchaseOrderId ? await getPurchaseOrderById(line.purchaseOrderId) : null;
            return { ...part, poNumber: po?.poNumber ?? part.poNumber ?? null };
          } catch (error) {
            console.error("Error loading purchase order for part", error);
          }
        }
        if (part?.source === "WAREHOUSE") {
          return { ...part, poNumber: null };
        }
        return part;
      }),
    );
    const servicesWithSource = await Promise.all(
      servicesData.map(async (service) => {
        if (service?.poNumber) {
          return service;
        }
        if (service?.source === "PURCHASE ORDER" && service?.poLineId) {
          try {
            const line = await getPurchaseOrderLineById(service.poLineId);
            const po = line?.purchaseOrderId ? await getPurchaseOrderById(line.purchaseOrderId) : null;
            return { ...service, poNumber: po?.poNumber ?? service.poNumber ?? null };
          } catch (error) {
            console.error("Error loading purchase order for service", error);
          }
        }
        return service;
      }),
    );
    setWorkOrderParts(partsWithSource);
    setWorkOrderEquipment(Array.isArray(equipment) ? equipment : []);
    setWorkOrderLabour(Array.isArray(labour) ? labour : []);
    setWorkOrderServices(servicesWithSource);
    setWorkOrderForms(Array.isArray(forms) ? forms : []);
  }, []);

  const fetchWorkOrderDetails = useCallback(async (workOrderId) => {
    if (!workOrderId) return;
    setWorkOrderDetailsLoading(true);
    try {
      const workOrder = await getWorkOrderById(workOrderId);
      setWorkOrderDetails(workOrder);
      await loadWorkOrderRelatedData(workOrderId);
      setShowWorkOrderDetailsModal(true);
    } catch (error) {
      console.error("Error loading work order details", error);
      setWorkOrderDetails(null);
      setWorkOrderParts([]);
      setWorkOrderEquipment([]);
      setWorkOrderLabour([]);
      setWorkOrderServices([]);
      setWorkOrderForms([]);
    } finally {
      setWorkOrderDetailsLoading(false);
    }
  }, [loadWorkOrderRelatedData]);

  const assetLabel = (component) => {
    const assetNumber = component.assetNumber || component.asset?.assetNumber || "";
    const assetDescription = component.assetDescription || component.asset?.assetDescription || "";
    return [assetNumber, assetDescription].filter(Boolean).join(" - ");
  };

  const taskName = (component) => {
    return component.cycleGroupName ? component.cycleGroupName : component.name;
  };

  const handleReplaceComponent = useCallback(
    async (component) => {
      if (checkingExistingWorkOrders) {
        return;
      }

      setSelectedComponent(component);
      const assetId = component?.assetId ?? null;
      const taskId = component?.id ?? null;

      if (!assetId || !taskId) {
        setExistingWorkOrder(null);
        setReplaceMode("create");
        setShowReplaceModal(true);
        return;
      }

      setCheckingExistingWorkOrders(true);
      setMatchingWorkOrders([]);
      setShowWorkOrderSelectionModal(false);

      try {
        const response = await getPaginatedWorkOrders({
          page: 1,
          pageSize: 25,
          assetId,
          taskId,
        });
        const workOrders = Array.isArray(response?.data) ? response.data : [];
        const filtered = workOrders.filter((workOrder) => {
          const isComponentTask = Number(workOrder?.task?.taskType?.component) === 1;
          const statusName = workOrder?.status?.name?.toLowerCase?.() || "";
          const isIncomplete = statusName !== "complete";
          return isComponentTask && isIncomplete;
        });

        if (filtered.length > 0) {
          setMatchingWorkOrders(filtered);
          setShowWorkOrderSelectionModal(true);
        } else {
          setExistingWorkOrder(null);
          setReplaceMode("create");
          setShowReplaceModal(true);
        }
      } catch (error) {
        console.error("Error checking existing work orders:", error);
        setExistingWorkOrder(null);
        setReplaceMode("create");
        setShowReplaceModal(true);
      } finally {
        setCheckingExistingWorkOrders(false);
      }
    },
    [checkingExistingWorkOrders],
  );

  const getFocusClass = (lifeRemaining) => {
    // Chnage background color of life remain using setting "component_life_warning"
    const thresholds = focusThresholds?.length ? focusThresholds : defaultFocusThresholds;
    for (const { class: cls, max } of thresholds) {
      if (lifeRemaining <= max / 100) return `bg-${cls}`;
    }

    return `bg-${thresholds[thresholds.length - 1].class}`;
  };

  const handleCompleteExistingWorkOrder = useCallback(
    async (workOrderSummary) => {
      if (!workOrderSummary) {
        return;
      }

      setCheckingExistingWorkOrders(true);
      try {
        const detailedWorkOrder = await getWorkOrderById(workOrderSummary.id);
        setExistingWorkOrder(detailedWorkOrder || workOrderSummary);
        setReplaceMode("edit");
        setShowWorkOrderSelectionModal(false);
        setMatchingWorkOrders([]);
        setShowReplaceModal(true);
      } catch (error) {
        console.error("Error loading work order:", error);
      } finally {
        setCheckingExistingWorkOrders(false);
      }
    },
    [],
  );

  const getDateString = (date) => date.toISOString().split('T')[0];
  const todayDateForFilter = new Date();
  const sevenDaysLaterForFilter = new Date(todayDateForFilter);
  sevenDaysLaterForFilter.setDate(todayDateForFilter.getDate() + 7);
  const [customNextStart, setCustomNextStart] = useState(startDateParam ?? getDateString(todayDateForFilter));
  const [customNextEnd, setCustomNextEnd] = useState(endDateParam ?? getDateString(sevenDaysLaterForFilter)); 
  // Refs keep the renderAfter callback stable (no filterConfig churn on date change)
  const customNextStartRef = useRef(customNextStart);
  const customNextEndRef = useRef(customNextEnd);
  customNextStartRef.current = customNextStart;
  customNextEndRef.current = customNextEnd;
  const renderNextDateAfter = useCallback((currentTempFilters) => {
    if (currentTempFilters.nextDateRange !== "custom") return null;
    return (
      <div className="col-12 mt-2">
        <div className="row">
          <div className="col-md-6 d-flex flex-column">
            <label className="form-label">Next Date Start</label>
            <input
              type="date"
              className="form-control"
              value={customNextStartRef.current}
              onChange={(e) => setCustomNextStart(e.target.value)}
            />
          </div>
          <div className="col-md-6 d-flex flex-column">
            <label className="form-label">Next Date End</label>
            <input
              type="date"
              className="form-control"
              value={customNextEndRef.current}
              onChange={(e) => setCustomNextEnd(e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  }, []);

  const filterConfig = useMemo(
    () => [
      {
        field: "departmentIds",
        type: "dropDown",
        label: "Site or Department",
        api: "hierarchy-structures",
        displayField: "name",
        menuBarFilter: 1
      },
      {
        field: "costResponsilityId",
        type: "dropDown",
        label: "Cost Responsibility",
        api: "cost-responsibility",
        displayField: "name",
        menuBarFilter: 1,
      },
      {
        field: "assetId",
        type: "dropDown",
        label: "Asset",
        api: "assets/for-dropdown",
        displayField: (a) => `${a.unitNumber || a.assetNumber || ""} ${a.assetDescription || ""}`.trim(),
        menuBarFilter: 0
      },
      { field: "code", type: "dropDown", label: "Task Code", api: "task-codes", displayField: "name", menuBarFilter: 1 },
      { field: "modifierCode", type: "dropDown", label: "Modifier Code", api: "modifier-codes", displayField: "name" },
      { field: "assetGroupClassId", type: "dropDown", label: "Asset Class", api: "class", displayField: "className" },
      { field: "assetGroupId", type: "dropDown", label: "Asset Group", api: "groups", displayField: "groupName", menuBarFilter: 1 },
      {
        field: "assetGroupManufacturerId",
        type: "dropDown",
        label: "Manufacturer",
        api: "manufacturers",
        displayField: "manufacturer",
      },
      {
        field: "defaultCostCentre",
        type: "dropDown",
        label: "Default Cost Centre",
        api: "cost-centres",
        displayField: (centre) =>
          `${centre.costCentreCode || ""} ${centre.costCenterDescription || ""}`.trim() || centre.costCenterDescription,
      },
      {
        field: "riskOpportunity",
        type: "dropDown",
        label: "Risk/Opportunity",
        options: [
          { value: "risk", label: "Risk" },
          { value: "opportunity", label: "Opportunity" },
        ],
        menuBarFilter: 0,
      },
      {
        field: "nextDateRange",
        type: "dropDown",
        label: "Next Date",
        isMulti: false,
        options: [
          { value: 7, label: "Next 7 Days" },
          { value: 14, label: "Next 14 Days" },
          { value: 30, label: "Next 30 Days" },
          { value: 60, label: "Next 60 Days" },
          { value: 90, label: "Next 90 Days" },
          { value: 180, label: "Next 180 Days" },
          { value: "custom", label: "Custom Date Range" },
        ],
        menuBarFilter: 0,
        maxMenuHeight: 330,
        renderAfter: renderNextDateAfter,
      },
      { field: "planningTask", type: "switch", label: "Planning Task" },
      { field: "statutory", type: "switch", label: "Statutory" },
      { field: "overdueOnly", type: "switch", label: "Overdue" },
    ],
    [renderNextDateAfter],
  );

  // Only set nextDateRange:"custom" when there is no dataFilterType — when dataFilterType IS
  // present the startDate/endDate are used for operational-dashboard work-order filtering, not
  // the task next-occurrence date filter, so we must not activate the "Custom Date Range" filter.
  const urlInitialFilters = {
    ...(overdueParam === "true" && { overdueOnly: true }),
    ...((startDateParam || endDateParam) && { nextDateRange: "custom" }),
  };

  const {
    appliedFilters,
    appliedFilterParams,
    renderFilterControls,
    filterOptions,
    setTempFiltersBatch,
} = useTableFilters(filterConfig, urlInitialFilters, {
    onReset: () => {
      setSearchTerm("");
      lastComponentRequestKeyRef.current = "";
      lastCalendarRequestKeyRef.current = "";
    },
    onClearFilters: clearFilterQueryParams,
  });

  const initialDepartmentIdsAppliedRef = useRef(false);
  const departmentIdOptions = filterOptions['departmentIds'];
  useEffect(() => {
    if (initialDepartmentIdsAppliedRef.current) return;
    if (!initialDepartmentIdsParam) return;
    if (!departmentIdOptions?.length) return;

    initialDepartmentIdsAppliedRef.current = true;
    const ids = initialDepartmentIdsParam
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);
    if (ids.length > 0) {
      setTempFiltersBatch({ departmentIds: ids }, { apply: true });
    }
  }, [initialDepartmentIdsParam, departmentIdOptions]);

  const appliedNextDateRange = appliedFilterParams.nextDateRange;
  useEffect(() => {
    if (appliedNextDateRange != null && appliedNextDateRange !== "") {
      setSortField("nextDate");
      setSortOrder("asc");
    }
  }, [appliedNextDateRange]);

  const resetPagination = useCallback(() => {
    setComponents([]);
    setPage(1);
    setHasMore(true);
    setTaskComments({});
    commentFetchInProgress.current.clear();
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    resetPagination();
  }, [appliedFilters, activeSearchTerm, resetPagination]);

  useEffect(() => {
    if (!hasInitialTaskTypeRunRef.current) {
      hasInitialTaskTypeRunRef.current = true;
      return;
    }

    resetPagination();
    setReloadKey((prev) => prev + 1);
  }, [taskType, resetPagination]);

  const buildFilterParams = useCallback(() => {
    const params = { ...appliedFilterParams };
    params.taskType = taskType;

    ["planningTask", "statutory", "overdueOnly"].forEach((key) => {
      if (typeof params[key] === "boolean") {
      if (params[key]) {
          params[key] = "true";
        } else {
        delete params[key];
        }
      } else if (params[key] === undefined || params[key] === null || params[key] === "") {
        delete params[key];
      }
    });

    [
      "assetId",
      "assetGroupId",
      "assetGroupClassId",
      "assetGroupManufacturerId",
      "departmentIds",
      "defaultCostCentre",
      "costResponsilityId",
      "code",
      "modifierCode",
      "riskOpportunity",
    ].forEach((key) => {
      if (Array.isArray(params[key]) && params[key].length === 0) {
        delete params[key];
      }
    });

    if (params.nextDateRange === "custom") {
      delete params.nextDateRange;
      params.nextDateStart = customNextStart;
      params.nextDateEnd = customNextEnd;
    } else if (params.nextDateRange === null || params.nextDateRange === "" || params.nextDateRange === undefined) {
      delete params.nextDateRange;
    }

    const riskOpportunityFilters = params.riskOpportunity;
    if (Array.isArray(riskOpportunityFilters)) {
      if (riskOpportunityFilters.includes("risk")) {
        params.componentRisk = "1";
      } else {
        delete params.componentRisk;
      }

      if (riskOpportunityFilters.includes("opportunity")) {
        params.componentOpportunity = "1";
      } else {
        delete params.componentOpportunity;
      }
    } else {
      delete params.componentRisk;
      delete params.componentOpportunity;
    }
    delete params.riskOpportunity;

    if (isComplianceTaskType) {
      params.statutory = "true";
    }

    if (assetParam) {
      params.assetId = assetParam;
    } 

    if (departmentParam) {
      params.departmentId = departmentParam;
    } else {
      delete params.departmentId;
    }

    if (dataFilterTypeParam) {
      params.dataFilterType = dataFilterTypeParam;
      if (startDateParam) params.startDate = startDateParam;
      if (endDateParam) params.endDate = endDateParam;
    } else {
      delete params.dataFilterType;
    }

    const resolvedDataFromElement =
      dataFromElementParam ||
      (isServiceTaskType
        ? "services"
        : isInspectionTaskType
          ? "inspection"
          : isComplianceTaskType
            ? "compliance"
            : undefined);
    if (resolvedDataFromElement) {
      params.dataFromElement = resolvedDataFromElement;
      console.log('params.dataFromElement', params.dataFromElement);
    } else {
      delete params.dataFromElement;
    }

    return params;
  }, [
    appliedFilterParams,
    isComplianceTaskType,
    isInspectionTaskType,
    isServiceTaskType,
    taskType,
    assetParam,
    dataFilterTypeParam,
    dataFromElementParam,
    customNextStart,
    customNextEnd,
    startDateParam,
    endDateParam,
  ]);

  useEffect(() => {
    const fetchComponents = async () => {
      const requestKey = JSON.stringify({
        page,
        taskType,
        viewMode,
        search: activeSearchTerm,
        sortField: sortField || null,
        sortOrder: sortOrder || null,
        reloadKey,
        filters: buildFilterParams(),
      });

      if (lastComponentRequestKeyRef.current === requestKey) {
        return;
      }
      lastComponentRequestKeyRef.current = requestKey;

      const requestId = componentFetchRequestIdRef.current + 1;
      componentFetchRequestIdRef.current = requestId;
      setLoading(true);
      try {
        const firstPageSize = 25;
        const subsequentPageSize = 5;
        const pageSize = page === 1 ? firstPageSize : subsequentPageSize;
        const apiPage = page === 1 ? 1 : page + firstPageSize / subsequentPageSize - 1;

        const params = buildFilterParams();

        const response = await getComponents({
          page: apiPage,
          pageSize,
          search: activeSearchTerm,
          sortField: sortField || undefined,
          sortOrder: sortOrder || undefined,
          ...params,
        });

        if (requestId !== componentFetchRequestIdRef.current) {
          return;
        }

        if (response?.error) {
          showToastError(`${response.error} error`);
        }

        setComponents((prev) => {
          const incoming = Array.isArray(response.data) ? response.data : [];
          const incomingById = new Map(incoming.map((item) => [item.id, item]));
          const merged = prev.map((item) => incomingById.get(item.id) || item);
          const existingIds = new Set(prev.map((item) => item.id));
          const newItems = incoming.filter((item) => !existingIds.has(item.id));
          const updated = [...merged, ...newItems];
          setHasMore(updated.length < response.total);
          return updated;
        });
      } catch (error) {
        if (requestId !== componentFetchRequestIdRef.current) {
          return;
        }
        console.error("Error fetching components:", error);
        lastComponentRequestKeyRef.current = "";
      } finally {
        if (requestId === componentFetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchComponents();
  }, [page, buildFilterParams, activeSearchTerm, sortField, sortOrder, reloadKey]);

  useEffect(() => {
    const fetchCalendarComponents = async () => {
      if (viewMode !== "calendar") {
        setCalendarComponents([]);
        lastCalendarRequestKeyRef.current = "";
        return;
      }

      const requestKey = JSON.stringify({
        viewMode,
        search: activeSearchTerm,
        reloadKey,
        filters: buildFilterParams(),
      });

      if (lastCalendarRequestKeyRef.current === requestKey) {
        return;
      }
      lastCalendarRequestKeyRef.current = requestKey;

      const requestId = calendarFetchRequestIdRef.current + 1;
      calendarFetchRequestIdRef.current = requestId;
      setCalendarLoading(true);
      try {
        const params = buildFilterParams();
        const pageSize = 25;
        let currentPage = 1;
        let collected = [];
        let total = 0;

        while (true) {
          const response = await getComponents({
            page: currentPage,
            pageSize,
            search: activeSearchTerm,
            ...params,
          });

          if (requestId !== calendarFetchRequestIdRef.current) {
            return;
          }

          const incoming = Array.isArray(response?.data) ? response.data : [];
          total = Number(response?.total) || incoming.length;
          collected = [...collected, ...incoming];

          if (incoming.length === 0 || collected.length >= total) {
            break;
          }

          currentPage += 1;
        }

        setCalendarComponents(collected);
      } catch (error) {
        if (requestId !== calendarFetchRequestIdRef.current) {
          return;
        }
        console.error("Error fetching components for calendar:", error);
        setCalendarComponents([]);
        lastCalendarRequestKeyRef.current = "";
      } finally {
        if (requestId === calendarFetchRequestIdRef.current) {
          setCalendarLoading(false);
        }
      }
    };

    fetchCalendarComponents();
  }, [viewMode, buildFilterParams, activeSearchTerm, reloadKey]);

  const calendarEvents = useMemo(() => {
    const list = Array.isArray(calendarComponents) ? calendarComponents : [];

    return list.flatMap((component) => {
      const occurrences = Array.isArray(component?.usageOccurences)
        ? component.usageOccurences
        : Array.isArray(component?.usageOccurrences)
          ? component.usageOccurrences
          : [];

      const componentCode = typeof component?.code === "string" ? component.code : component?.code?.code;
      const componentName = component?.name || component?.cycleGroupName || "";
      const componentLabel = [componentCode, componentName].filter(Boolean).join(" ").trim();
      const assetNumber = component?.asset?.assetNumber || component?.asset?.unitNumber || component?.assetNumber || component?.unitNumber || "";
      const workOrders = [
        ...(Array.isArray(component?.pendingWorkOrder) ? component.pendingWorkOrder : []),
        ...(Array.isArray(component?.workOrders) ? component.workOrders : []),
        ...(Array.isArray(component?.taskWorkOrders) ? component.taskWorkOrders : []),
      ];
      const workOrderById = new Map(
        workOrders
          .filter((workOrder) => workOrder?.id != null)
          .map((workOrder) => [String(workOrder.id), workOrder]),
      );
      const workOrderByNumber = new Map(
        workOrders
          .filter((workOrder) => workOrder?.workOrderNumber)
          .map((workOrder) => [String(workOrder.workOrderNumber), workOrder]),
      );

      return occurrences
        .filter((occurrence) => occurrence?.timestamp)
        .map((occurrence, index) => {
          const matchedWorkOrder =
            workOrderById.get(String(occurrence?.workOrderId))
            || workOrderByNumber.get(String(occurrence?.workOrderNumber));
          const isWorkOrderEvent = Boolean(occurrence?.workOrderNumber);
          const workOrderStart = matchedWorkOrder?.scheduledAt || occurrence?.timestamp;
          const workOrderEnd = matchedWorkOrder?.dueDate || workOrderStart;

          return {
            id: `component-occ-${component?.id}-${index}-${occurrence?.timestamp}`,
            title: componentLabel || "Task occurrence",
            start: isWorkOrderEvent ? workOrderStart : occurrence.timestamp,
            end: isWorkOrderEvent ? workOrderEnd : undefined,
            allDay: !isWorkOrderEvent,
            taskId: component?.id,
            assetNumber,
            componentLabel,
            workOrderId: occurrence?.workOrderId || matchedWorkOrder?.id || null,
            workOrderNumber: occurrence?.workOrderNumber || "",
            hasWorkOrder: isWorkOrderEvent,
          };
        });
    });
  }, [calendarComponents]);

  useEffect(() => {
    const missingTaskIds = components
      .map((component) => component?.id)
      .filter((id) => id && !taskComments[id] && !commentFetchInProgress.current.has(id));

    if (missingTaskIds.length === 0) {
      return;
    }

    let cancelled = false;
    missingTaskIds.forEach((id) => commentFetchInProgress.current.add(id));

    /*const loadComments = async () => {
      const results = await Promise.all(
        missingTaskIds.map(async (taskId) => {
          try {
            const response = await getTaskComments(taskId);
            const comments = Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response)
                ? response
                : [];
            return { taskId, comments };
          } catch (error) {
            console.error("Error fetching task comments:", error);
            return { taskId, comments: [] };
          }
        }),
      );

      if (cancelled) {
        return;
      }

      setTaskComments((prev) => {
        const next = { ...prev };
        results.forEach(({ taskId, comments }) => {
          next[taskId] = sortCommentsDesc(comments);
        });
        return next;
      });
    };*/

    /*loadComments().finally(() => {
      missingTaskIds.forEach((id) => commentFetchInProgress.current.delete(id));
    });*/

    return () => {
      cancelled = true;
    };
  }, [components, sortCommentsDesc, taskComments]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        hasMore &&
        !loading
      ) {
        setPage((prev) => prev + 1);
      }
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [hasMore, loading]);

  const handleSortChange = useCallback(
    (field) => {
      if (sortField === field) {
        if (sortOrder === "asc") {
          setSortOrder("desc");
        } else if (sortOrder === "desc") {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder("asc");
        }
      } else {
        setSortField(field);
        setSortOrder("asc");
      }
      resetPagination();
    },
    [resetPagination, sortField, sortOrder],
  );

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return "mdi:sort";
    }
    return sortOrder === "asc" ? "mdi:sort-ascending" : "mdi:sort-descending";
  };

  const renderSortableHeader = (label, field) => {
    const ariaSort = sortField === field
      ? sortOrder === "asc"
        ? "ascending"
        : sortOrder === "desc"
          ? "descending"
          : "none"
      : "none";

    return (
      <th
        scope="col"
        onClick={() => handleSortChange(field)}
        style={{ cursor: "pointer" }}
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon className="text-xl" icon={getSortIcon(field)} />
        </span>
      </th>
    );
  };

  const workOrderStatusOptions = useMemo(
    () =>
      Array.isArray(workOrderStatuses)
        ? workOrderStatuses.map((status) => ({
          value: status.id ?? status.value,
          label: status.name ?? status.label,
          colour: status.colours?.bootstrapName,
          bootstrapName: status.bootstrapName,
        }))
        : [],
    [workOrderStatuses],
  );

  const workOrderGeneralFields = useMemo(
    () => [
      { field: 'workOrderTitle', label: 'Title', type: 'text', size: 8, required: true },
      ...(Number(useCostResponsibility) === 1
        ? [{
          field: 'costResponsibilityId',
          label: 'Cost Responsibility',
          type: 'select',
          size: 4,
          required: true,
          api: 'cost-responsibility',
          displayField: 'name',
        }]
        : []),
      {
        field: 'statusId',
        label: 'Status',
        type: 'select',
        size: 4,
        required: true,
        options: workOrderStatusOptions,
        formatOptionLabel: formatOptionColor,
      },
      { field: 'workOrderDescription', label: 'Description', type: 'textarea', size: 12, rows: 3 },
      {
        field: 'assetId',
        label: 'Asset',
        type: 'select',
        size: 4,
        required: true,
        options: workOrderAssetOption ? [workOrderAssetOption] : [],
        disabled: true,
      },
      {
        field: 'taskId',
        label: 'Task',
        type: 'select',
        size: 4,
        required: false,
        options: workOrderTaskOption ? [workOrderTaskOption] : [],
        disabled: true,
      },
      { field: 'breakdown', label: 'Breakdown', type: 'switch', size: 1, required: false, switchColor: 'danger' },
      { field: 'statutory', label: 'Compliance', type: 'switch', size: 1, required: false, switchColor: 'danger' },
      { field: 'taskCodeId', label: 'Task Code', type: 'select', size: 4, required: false, api: 'task-codes', displayField: 'name', isClearable: true },
      { field: 'taskTypeId', label: 'Task Type', type: 'select', size: 4, required: true, api: 'task-types', displayField: 'taskTypeName' },
      { field: 'priorityId', label: 'Priority', type: 'select', size: 4, required: true, api: 'priority', displayField: 'name', formatOptionLabel: formatOptionColor },
      ...buildWorkOrderStatusFields({
        newWorkOrder,
        editingId: workOrderEditingId,
        scheduleDisabled: false,
        startDisabled: newWorkOrder.statusId === 1,
        completeDisabled: [1, 2].includes(newWorkOrder.statusId),
        includeTeams: true,
        formatOptionColor,
        includeCrew: true,
        crewOptions,
      }),
    ],
    [
      formatOptionColor,
      newWorkOrder.statusId,
      workOrderAssetOption,
      workOrderEditingId,
      workOrderStatusOptions,
      workOrderTaskOption,
      useCostResponsibility,
      crewOptions,
    ],
  );

  const workOrderCompleteFields = useMemo(
    () =>
      buildWorkOrderCompleteFields({
        newWorkOrder,
        includeCompletionImages: false,
      }),
    [newWorkOrder.statusId],
  );

  const workOrderValidationFields = useMemo(
    () =>
      buildWorkOrderValidationFields({
        generalFields: workOrderGeneralFields,
        completeFields: workOrderCompleteFields,
        editingId: workOrderEditingId,
        statusId: newWorkOrder.statusId,
      }),
    [newWorkOrder.statusId, workOrderCompleteFields, workOrderEditingId, workOrderGeneralFields],
  );

  const {
    errors: workOrderErrors,
    setErrors: setWorkOrderErrors,
  } = useValidator(workOrderValidationFields, newWorkOrder);

  const handleOpenEditWorkOrder = useCallback(
    async (workOrderRef) => {
      const workOrderId = resolveWorkOrderId(workOrderRef);
      if (!workOrderId) return;
      setWorkOrderModalLoading(true);
      setShowWorkOrderDetailsModal(false);
      setWorkOrderGeneralOnly(false);
      setWorkOrderErrors({});
      setDeletedPartIds([]);
      setDeletedLabourIds([]);
      setDeletedServiceIds([]);
      setDeletedEquipmentIds([]);
      setDuration('');
      setActualDuration('');
      durationEditedRef.current = false;
      actualDurationEditedRef.current = false;
      prevScheduledAtRef.current = null;
      prevStartedAtRef.current = null;
      try {
        const workOrder = await getWorkOrderById(workOrderId);
        const assetOption = workOrder?.assetId
          ? { value: workOrder.assetId, label: assetLabel(workOrder) }
          : null;
        const taskOption = workOrder?.taskId
          ? { value: workOrder.taskId, label: taskName(workOrder.task ?? workOrder) }
          : null;
        setWorkOrderAssetOption(assetOption);
        setWorkOrderTaskOption(taskOption);
        setNewWorkOrder({
          ...getInitialWorkOrder(),
          ...workOrder,
          workorderFiles: Array.isArray(workOrder?.workorderFiles) ? workOrder.workorderFiles : [],
          workOrderImage: Array.isArray(workOrder?.workOrderImage) ? workOrder.workOrderImage : [],
          completionImages: Array.isArray(workOrder?.completionImages) ? workOrder.completionImages : [],
          costResponsibilityId: workOrder?.costResponsibilityId ?? 1,
        });
        await loadWorkOrderRelatedData(workOrderId);
        setWorkOrderEditingId(workOrderId);
        setShowAddWorkOrderModal(true);
      } catch (error) {
        console.error("Error loading work order:", error);
      } finally {
        setWorkOrderModalLoading(false);
      }
    },
    [assetLabel, loadWorkOrderRelatedData, resolveWorkOrderId, setWorkOrderErrors, taskName],
  );

  const handleWorkOrderLinkClick = useCallback(async (workOrder) => {
    const workOrderId = resolveWorkOrderId(workOrder);
    if (!workOrderId) return;
    if (canUpdateWorkOrders) {
      await handleOpenEditWorkOrder(workOrderId);
      return;
    }
    await fetchWorkOrderDetails(workOrderId);
  }, [canUpdateWorkOrders, fetchWorkOrderDetails, handleOpenEditWorkOrder, resolveWorkOrderId]);

  const handleCloseWorkOrderDetails = useCallback(() => {
    setShowWorkOrderDetailsModal(false);
    setSelectedWorkOrder(null);
    setWorkOrderDetails(null);
    setWorkOrderParts([]);
    setWorkOrderEquipment([]);
    setWorkOrderLabour([]);
    setWorkOrderServices([]);
    setWorkOrderForms([]);
  }, []);

  const updateComponentPendingWorkOrder = useCallback((workOrder) => {
    if (!workOrder?.taskId) return;
    setComponents((prev) =>
      prev.map((component) => {
        if (component.id !== workOrder.taskId) return component;
        const existingPending = Array.isArray(component.pendingWorkOrder)
          ? component.pendingWorkOrder[0]
          : null;
        const nextPending = {
          ...existingPending,
          id: workOrder.id ?? existingPending?.id,
          workOrderId: workOrder.id ?? existingPending?.workOrderId,
          workOrderNumber: workOrder.workOrderNumber ?? existingPending?.workOrderNumber,
          partsStatus: workOrder.partsStatus ?? existingPending?.partsStatus ?? '',
        };
        return { ...component, pendingWorkOrder: [nextPending] };
      }),
    );
  }, []);

  const hasReviewData = useCallback((task, workOrders) => {
    const occurrences = Array.isArray(task?.usageOccurences)
      ? task.usageOccurences
      : Array.isArray(task?.usageOccurrences)
        ? task.usageOccurrences
        : [];
    return occurrences.length > 0 || workOrders.length > 0;
  }, []);

  useEffect(() => {
    const statusId = Number(newWorkOrder.statusId);
    const prevStatusId = prevStatusIdRef.current;
    if (prevStatusId === null) {
      prevStatusIdRef.current = statusId;
      return;
    }

    if (statusId === 2 && prevStatusId !== 2) {
      let startedAtDate = newWorkOrder.scheduledAt
        ? new Date(newWorkOrder.scheduledAt)
        : new Date(Date.now() + 5 * 60 * 1000);
      if (Number.isNaN(startedAtDate.getTime())) {
        startedAtDate = new Date(Date.now() + 5 * 60 * 1000);
      }

      const savedDuration = parseFloat(duration);
      const scheduledDuration = Number.isFinite(savedDuration) && savedDuration >= 0 ? savedDuration : 0;
      scheduledDurationRef.current = scheduledDuration;

      const startedAtIso = startedAtDate.toISOString();
      const dueDateIso = new Date(
        startedAtDate.getTime() + scheduledDuration * 3600000,
      ).toISOString();

      setNewWorkOrder((prev) => ({
        ...prev,
        startedAt: startedAtIso,
        dueDate: dueDateIso,
      }));

      if (!Number.isFinite(savedDuration) || savedDuration < 0) {
        setDuration(String(scheduledDuration));
      }
      durationEditedRef.current = false;
      prevScheduledAtRef.current = startedAtIso;
    }

    prevStatusIdRef.current = statusId;
  }, [duration, newWorkOrder.scheduledAt, newWorkOrder.statusId]);

  useEffect(() => {
    const statusId = Number(newWorkOrder.statusId);
    const durationBase = statusId === 2 ? newWorkOrder.startedAt : newWorkOrder.scheduledAt;
    if (!durationBase || !newWorkOrder.dueDate) return;
    const start = new Date(durationBase);
    const end = new Date(newWorkOrder.dueDate);
    const diffMs = end - start;
    if (!Number.isNaN(diffMs)) {
      const hrs = Math.max(0, diffMs / 3600000);
      const val = hrs.toFixed(2);
      if (!durationEditedRef.current && val !== duration) {
        setDuration(val);
      }
    }
    setWorkOrderErrors((prev) => {
      if (statusId === 2 && end <= start) {
        return { ...prev, dueDate: 'Estimated completion must be after started time' };
      }
      if ((statusId === 1 || statusId === 5) && end <= start) {
        return { ...prev, dueDate: 'Due date cannot be before scheduled start' };
      }
      const { dueDate, ...rest } = prev;
      return rest;
    });
  }, [duration, newWorkOrder.dueDate, newWorkOrder.scheduledAt, newWorkOrder.startedAt, newWorkOrder.statusId, setWorkOrderErrors]);

  useEffect(() => {
    const statusId = Number(newWorkOrder.statusId);
    const durationBase = statusId === 2 ? newWorkOrder.startedAt : newWorkOrder.scheduledAt;
    if (!durationBase || duration === '') return;
    if (statusId === 2 && !durationEditedRef.current) return;
    if (!durationEditedRef.current && prevScheduledAtRef.current === durationBase) return;
    const start = new Date(durationBase);
    const hrs = parseFloat(duration);
    if (Number.isNaN(hrs)) return;
    const end = new Date(start.getTime() + hrs * 3600000);
    const iso = end.toISOString();
    if (newWorkOrder.dueDate !== iso) {
      setNewWorkOrder((prev) => ({ ...prev, dueDate: iso }));
    }
    prevScheduledAtRef.current = durationBase;
  }, [duration, newWorkOrder.dueDate, newWorkOrder.scheduledAt, newWorkOrder.startedAt, newWorkOrder.statusId]);

  useEffect(() => {
    if (!newWorkOrder.startedAt || !newWorkOrder.completedAt) {
      if (!actualDurationEditedRef.current) {
        setActualDuration('');
      }
      actualDurationEditedRef.current = false;
      pendingActualDurationSyncRef.current = false;
      prevCompletedAtRef.current = newWorkOrder.completedAt ?? null;
      setWorkOrderErrors((prev) => {
        const { completedAt, ...rest } = prev;
        return rest;
      });
      return;
    }

    if (
      pendingActualDurationSyncRef.current
      && prevCompletedAtRef.current === newWorkOrder.completedAt
    ) {
      return;
    }

    const start = new Date(newWorkOrder.startedAt);
    const end = new Date(newWorkOrder.completedAt);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    if (end < start) {
      setWorkOrderErrors((prev) => ({ ...prev, completedAt: 'Completed time cannot be before start time' }));
      return;
    }
    setWorkOrderErrors((prev) => {
      const { completedAt, ...rest } = prev;
      return rest;
    });
    const diffMs = end - start;
    const hrs = Math.max(0, diffMs / 3600000);
    const val = hrs.toFixed(2);
    if (!actualDurationEditedRef.current && val !== actualDuration) {
      setActualDuration(val);
    }
    if (pendingActualDurationSyncRef.current && prevCompletedAtRef.current !== newWorkOrder.completedAt) {
      pendingActualDurationSyncRef.current = false;
    }
    prevCompletedAtRef.current = newWorkOrder.completedAt;
  }, [actualDuration, newWorkOrder.completedAt, newWorkOrder.startedAt, setWorkOrderErrors]);

  useEffect(() => {
    if (newWorkOrder.statusId === 4) return;
    setWorkOrderErrors((prev) => {
      const { actualDuration, ...rest } = prev;
      return rest;
    });
  }, [newWorkOrder.statusId, setWorkOrderErrors]);

  const { renderField: renderWorkOrderField } = useFormBuilder(
    workOrderGeneralFields,
    newWorkOrder,
    setNewWorkOrder,
    workOrderErrors,
  );

  const { renderField: renderCompleteFields } = useFormBuilder(
    workOrderCompleteFields,
    newWorkOrder,
    setNewWorkOrder,
    workOrderErrors,
  );

  const { renderField: renderProgressFields } = useFormBuilder(
    workOrderCompleteFields,
    newWorkOrder,
    setNewWorkOrder,
    workOrderErrors,
  );

  const workOrderDocumentFields = useMemo(
    () => [
      {
        field: 'workorderFiles',
        label: 'Documents',
        type: 'files',
        size: 12,
        directory: 'workorderfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const { renderField: renderDocumentField } = useFormBuilder(
    workOrderDocumentFields,
    newWorkOrder,
    setNewWorkOrder,
    workOrderErrors,
  );

  const isWorkOrderLocked = useMemo(
    () => [4, 5].includes(Number(newWorkOrder?.statusId)),
    [newWorkOrder?.statusId],
  );

  const normalizeDurationValue = useCallback((value) => {
    if (value === '' || value === null || value === undefined) return value;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return value;
    const [, decimals = ''] = String(value).split('.');
    if (decimals.length <= 2) return String(value);
    const truncated = Math.trunc(numeric * 100) / 100;
    return truncated.toFixed(2);
  }, []);

  const handleDurationChange = (value) => {
    durationEditedRef.current = true;
    setDuration(value);
  };

  const handleActualDurationChange = (value) => {
    actualDurationEditedRef.current = true;
    setActualDuration(value);
  };

  const handleDurationBlur = () => {
    const num = parseFloat(duration);
    if (duration === '' || Number.isNaN(num) || num < 0 || num > 9999) {
      setWorkOrderErrors((prev) => ({ ...prev, duration: 'Duration must be between 0 and 9999 hours' }));
      durationEditedRef.current = true;
      return;
    }
    setWorkOrderErrors((prev) => {
      const { duration, ...rest } = prev;
      return rest;
    });
    durationEditedRef.current = false;
  };

  const handleActualDurationBlur = () => {
    const num = parseFloat(actualDuration);
    if (actualDuration === '' || Number.isNaN(num) || num < 0 || num > 9999) {
      setWorkOrderErrors((prev) => ({ ...prev, actualDuration: 'Actual duration must be between 0 and 9999 hours' }));
      actualDurationEditedRef.current = true;
      return;
    }
    setWorkOrderErrors((prev) => {
      const { actualDuration, ...rest } = prev;
      return rest;
    });
  };

  const buildScheduledAt = useCallback(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    const [hoursRaw, minutesRaw] = String(shiftStartTime || '08:00').split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    date.setHours(Number.isNaN(hours) ? 0 : hours, Number.isNaN(minutes) ? 0 : minutes, 0, 0);
    return date.toISOString();
  }, [shiftStartTime]);

  const handleOpenAddWorkOrder = useCallback(
    (component) => {
      if (!component) return;
      const initialWorkOrder = getInitialWorkOrder();
      const scheduledAt = buildScheduledAt();
      const duration = Number(component?.duration);
      const durationValue = Number.isFinite(duration) ? duration.toFixed(2) : '';
      let dueDate = null;
      if (scheduledAt && Number.isFinite(duration)) {
        const due = new Date(scheduledAt);
        due.setHours(due.getHours() + duration);
        dueDate = due.toISOString();
      }
      const assetOption = component?.assetId
        ? { value: component.assetId, label: assetLabel(component) }
        : null;
      const taskOption = component?.id
        ? { value: component.id, label: taskName(component) }
        : null;
      const firstNullWO = component?.usageOccurrences.find(o => o.workOrderNumber === null) || null;
      const cyclePriority = firstNullWO?.cyclePriority ?? null;
      setWorkOrderAssetOption(assetOption);
      setWorkOrderTaskOption(taskOption);
      setWorkOrderEditingId(null);
      setWorkOrderGeneralOnly(true);
      setWorkOrderErrors({});
      setWorkOrderParts([]);
      setWorkOrderLabour([]);
      setWorkOrderServices([]);
      setWorkOrderEquipment([]);
      setDeletedPartIds([]);
      setDeletedLabourIds([]);
      setDeletedServiceIds([]);
      setDeletedEquipmentIds([]);
      setDuration(durationValue);
      setActualDuration('');
      durationEditedRef.current = false;
      actualDurationEditedRef.current = false;
      prevScheduledAtRef.current = null;
      prevStartedAtRef.current = null;

      setNewWorkOrder({
        ...initialWorkOrder,
        assetId: component?.assetId ?? null,
        taskId: component?.id ?? null,
        taskTypeId: component?.taskTypeId ?? initialWorkOrder.taskTypeId,
        taskCodeId: component?.codeId ?? component?.taskCodeId ?? null,
        workOrderTitle: component?.code?.name ?? '',
        cycleGroupOrder: cyclePriority,
        scheduledAt,
        dueDate,
        statutory: component?.statutory === 1 ? 1 : 0,
      });
      setShowAddWorkOrderModal(true);
    },
    [assetLabel, buildScheduledAt, setWorkOrderErrors, taskName],
  );

  const handleCloseWorkOrderModal = useCallback(() => {
    setShowAddWorkOrderModal(false);
    setWorkOrderEditingId(null);
    setWorkOrderGeneralOnly(true);
    setWorkOrderErrors({});
    setNewWorkOrder(getInitialWorkOrder());
    setWorkOrderParts([]);
    setWorkOrderLabour([]);
    setWorkOrderServices([]);
    setWorkOrderEquipment([]);
    setDeletedPartIds([]);
    setDeletedLabourIds([]);
    setDeletedServiceIds([]);
    setDeletedEquipmentIds([]);
    setDuration('');
    setActualDuration('');
    durationEditedRef.current = false;
    actualDurationEditedRef.current = false;
    prevScheduledAtRef.current = null;
    prevStartedAtRef.current = null;
  }, []);

  const refreshComponentTable = useCallback(() => {
    setComponents([]);
    setPage(1);
    setHasMore(true);
    setReloadKey((prev) => prev + 1);
  }, []);

  const handleWorkOrderFileDelete = useCallback((_, idx) => {
    setNewWorkOrder((prev) => {
      const nextFiles = Array.isArray(prev.workorderFiles)
        ? prev.workorderFiles.filter((__, i) => i !== idx)
        : prev.workorderFiles;
      return { ...prev, workorderFiles: nextFiles };
    });
  }, []);

  const clearEquipmentFieldError = useCallback(
    (field) => {
      setEquipmentErrors((prev) => {
        if (!prev || !prev[field]) return prev;
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [],
  );

  const clearLabourFieldError = useCallback(
    (field) => {
      setLabourErrors((prev) => {
        if (!prev || !prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [],
  );

  const handleSaveEquipment = useCallback(() => {
    const errors = {};
    if (!equipmentForm.name) {
      errors.name = 'Name is required';
    }
    if (!equipmentForm.quantity || Number(equipmentForm.quantity) <= 0) {
      errors.quantity = 'Quantity is required';
    }
    if (Object.keys(errors).length > 0) {
      setEquipmentErrors(errors);
      return;
    }
    const payload = {
      ...equipmentForm,
      quantity: Number(equipmentForm.quantity),
      cost: equipmentForm.cost === '' || equipmentForm.cost === null
        ? 0
        : Number(equipmentForm.cost),
    };
    setWorkOrderEquipment((prev) => {
      if (editingEquipmentIndex !== null) {
        return prev.map((item, idx) => (idx === editingEquipmentIndex ? payload : item));
      }
      return [...prev, payload];
    });
    setShowEquipmentModal(false);
  }, [editingEquipmentIndex, equipmentForm]);

  const handleSaveLabour = useCallback(() => {
    const errors = {};
    if (!labourForm.tradeId) {
      errors.tradeId = 'Trade is required';
    }
    if (!labourForm.qty || Number(labourForm.qty) <= 0) {
      errors.qty = 'Quantity is required';
    }
    if (!labourForm.labourHours || Number(labourForm.labourHours) <= 0) {
      errors.labourHours = 'Hours are required';
    }
    if (Object.keys(errors).length > 0) {
      setLabourErrors(errors);
      return;
    }
    const payload = {
      ...labourForm,
      qty: Number(labourForm.qty),
      labourHours: Number(labourForm.labourHours),
      labourRate: labourForm.labourRate === '' || labourForm.labourRate === null
        ? 0
        : Number(labourForm.labourRate),
    };
    setWorkOrderLabour((prev) => {
      if (editingLabourIndex !== null) {
        return prev.map((item, idx) => (idx === editingLabourIndex ? payload : item));
      }
      return [...prev, payload];
    });
    setShowLabourModal(false);
  }, [editingLabourIndex, labourForm]);

  const closeFormModal = useCallback(() => {
    setShowFormModal(false);
    setFormTargetWorkOrder(null);
  }, []);

  const handleViewForm = useCallback((form) => {
    setViewWorkOrderFormId(form.id);
    setShowFormView(true);
  }, []);

  const handlePrintForm = useCallback((pdfLink) => {
    if (!pdfLink) return;
    window.open(pdfLink, '_blank', 'noopener,noreferrer');
  }, []);

  const openAddFormModal = useCallback(async (workOrderOverride = null) => {
    try {
      const [assignments, forms] = await Promise.all([
        getFormAssignments(),
        getForms(),
      ]);
      const targetWo = workOrderOverride || newWorkOrder;
      const targetWorkOrderId = workOrderOverride?.id || targetWo?.id || workOrderEditingId;
      if (!targetWorkOrderId) {
        console.warn('No work order selected for adding forms');
        return;
      }
      let assetClassId = null;
      let assetGroupId = null;
      if (targetWo?.assetId) {
        const asset = await getAssetById(targetWo.assetId);
        assetClassId = asset?.group?.classId;
        assetGroupId = asset?.groupId;
      }
      const parseArr = (val) => {
        if (!val) return [];
        let arr = [];
        if (Array.isArray(val)) arr = val;
        else if (typeof val === 'object') {
          try {
            arr = Array.isArray(val.ids) ? val.ids : Object.values(val);
          } catch {
            arr = [];
          }
        } else {
          arr = [val];
        }
        return arr.map((n) => parseInt(n, 10));
      };
      const filtered = assignments.filter((assignment) => {
        const ac = parseArr(assignment.assetClassIds);
        const ag = parseArr(assignment.assetGroupIds);
        const tm = parseArr(assignment.teamIds);
        const dp = parseArr(assignment.departmentIds);
        const allClass = ac.includes(-1);
        const allGroup = ag.includes(-1);
        const allTeam = tm.includes(-1);
        const allDept = dp.includes(-1);

        const classMatch = allClass || ac.includes(assetClassId);
        const groupMatch = allGroup || ag.includes(assetGroupId);
        const teamMatch = allTeam || tm.includes(targetWo?.teamsId);
        const deptMatch = allDept || dp.includes(targetWo?.departmentId);

        return classMatch || groupMatch || teamMatch || deptMatch;
      });
      const ids = [...new Set(filtered.map((f) => f.formId))];
      const existingFormIds = workOrderForms.map((wf) => wf.formId);
      const opts = forms
        .filter((form) => ids.includes(form.id) && !existingFormIds.includes(form.id))
        .map((form) => ({ value: form.id, label: form.name }));
      setFormOptions(opts);
      setSelectedFormId('');
      setFormTargetWorkOrder({
        id: targetWorkOrderId,
        assetId: targetWo?.assetId || null,
        areaId: targetWo?.areaId || 0,
        departmentId: targetWo?.departmentId || null,
        teamId: targetWo?.teamsId || null,
      });
      setShowFormModal(true);
    } catch (err) {
      console.error('Error loading forms for work order', err);
    }
  }, [newWorkOrder, workOrderEditingId, workOrderForms]);

  const handleSaveFormSelection = useCallback(async () => {
    if (!selectedFormId) return;
    const targetContext = formTargetWorkOrder || {
      id: workOrderEditingId,
      assetId: newWorkOrder.assetId || null,
      areaId: newWorkOrder.areaId || 0,
      departmentId: newWorkOrder.departmentId || null,
      teamId: newWorkOrder.teamsId || null,
    };
    if (!targetContext?.id) {
      console.warn('No work order selected for saving forms');
      return;
    }
    const userId = userProfile?.id ?? null;
    try {
      const newWoForm = await createWorkOrderForm({
        formId: parseInt(selectedFormId, 10),
        workOrderId: targetContext.id,
        assetId: targetContext.assetId,
        locationId: targetContext.areaId,
        createdBy: userId,
      });
      const forms = await getWorkOrderFormsByWorkOrderId(targetContext.id);
      setWorkOrderForms(forms);

      try {
        const form = await getFormById(parseInt(selectedFormId, 10));
        const items = form.tblFormItems || [];
        await Promise.all(
          items.map((item) =>
            createFormData({
              formItemId: item.id,
              formId: item.formId,
              workOrderFormId: newWoForm.id,
              description: item.description,
              width: item.width,
              images: item.images,
              heading: item.heading,
              formInputTypeId: item.formInputTypeId,
              instructions: item.instructions,
              includePhoto: item.includePhoto,
              disableComments: item.disableComments,
              raiseDefects: item.raiseDefects,
              required: item.required,
              data: item.data || null,
              sortOrder: item.sortOrder,
              assetId: targetContext.assetId,
              departmentId: targetContext.departmentId,
              teamId: targetContext.teamId,
              createdBy: userId,
            }),
          ),
        );
        const actions = form.tblFormActions || [];
        await Promise.all(
          actions.map((action) =>
            createWorkOrderFormAction({
              workOrderFormId: newWoForm.id,
              actionData: action.actionData,
              formActionId: action.formActionId,
              createdBy: userId,
            }),
          ),
        );
      } catch (copyErr) {
        console.error('Error copying form items or actions', copyErr);
      }

      setShowFormModal(false);
      setFormTargetWorkOrder(null);
    } catch (err) {
      console.error('Error saving work order form', err);
    }
  }, [formTargetWorkOrder, newWorkOrder, selectedFormId, userProfile, workOrderEditingId]);

  const sanitizeServicePayload = useCallback((service) => {
    const { ...payload } = service;
    delete payload.purchaseOrderNumber;
    delete payload.poNumber;
    return payload;
  }, []);

  const handleSaveWorkOrder = useCallback(async () => {
    const validationErrors = {
      ...validateForm(workOrderValidationFields, newWorkOrder),
      ...getWorkOrderStatusValidationErrors({
        newWorkOrder,
        editingId: workOrderEditingId,
        duration,
        actualDuration,
      }),
    };
    if (Object.keys(validationErrors).length > 0) {
      setWorkOrderErrors(validationErrors);
      return;
    }

    const normalizedDuration = normalizeDurationValue(duration);
    const shouldNormalizeDuration = normalizedDuration !== duration;
    if (shouldNormalizeDuration) {
      setDuration(normalizedDuration);
    }
    let dueDateOverride = null;
    if (normalizedDuration !== '' && normalizedDuration !== null && normalizedDuration !== undefined && newWorkOrder.scheduledAt) {
      const start = new Date(newWorkOrder.scheduledAt);
      const hrs = parseFloat(normalizedDuration);
      if (!Number.isNaN(start.getTime()) && Number.isFinite(hrs)) {
        const end = new Date(start.getTime() + hrs * 3600000);
        dueDateOverride = end.toISOString();
        if (newWorkOrder.dueDate !== dueDateOverride) {
          setNewWorkOrder((prev) => ({ ...prev, dueDate: dueDateOverride }));
        }
      }
    }

    setWorkOrderModalLoading(true);
    let didSaveWorkOrder = false;
    try {
      const { createEvent: _createEvent, ...workOrderBase } = newWorkOrder;
      const sanitizedWorkOrderBase = Object.entries(workOrderBase).reduce((acc, [key, value]) => {
        if (value === null || value === undefined) {
          acc[key] = value;
          return acc;
        }
        if (Array.isArray(value) || typeof value !== 'object') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const payload = {
        ...sanitizedWorkOrderBase,
        assetId: newWorkOrder.assetId ? Number(newWorkOrder.assetId) : null,
        taskId: newWorkOrder.taskId ? Number(newWorkOrder.taskId) : null,
        taskCodeId: newWorkOrder.taskCodeId ?? null,
        taskTypeId: Number(newWorkOrder.taskTypeId ?? newWorkOrder.taskType?.id),
        priorityId: Number(newWorkOrder.priorityId ?? newWorkOrder.priority?.id),
        statusId: Number(newWorkOrder.statusId ?? newWorkOrder.status?.id),
        breakdown: Number(newWorkOrder.breakdown ?? 0),
        statutory: Number(newWorkOrder.statutory ?? 0),
        scheduledAt: newWorkOrder.scheduledAt ? toUtcIsoWithTime(newWorkOrder.scheduledAt) : null,
        dueDate: (dueDateOverride ?? newWorkOrder.dueDate)
          ? toUtcIsoWithTime(dueDateOverride ?? newWorkOrder.dueDate)
          : null,
        startedAt: newWorkOrder.startedAt ? toUtcIsoWithTime(newWorkOrder.startedAt) : null,
        completedAt: newWorkOrder.completedAt ? toUtcIsoWithTime(newWorkOrder.completedAt) : null,
      };

      if (workOrderEditingId) {
        await updateWorkOrder(workOrderEditingId, payload);
        didSaveWorkOrder = true;
        if (deletedPartIds.length > 0) {
          await Promise.all(deletedPartIds.map((id) => deleteOne('work-order-parts', id)));
          setDeletedPartIds([]);
        }
        if (deletedEquipmentIds.length > 0) {
          await Promise.all(deletedEquipmentIds.map((id) => deleteOne('bill-of-equipment-wos', id)));
          setDeletedEquipmentIds([]);
        }
        if (deletedLabourIds.length > 0) {
          await Promise.all(deletedLabourIds.map((id) => deleteOne('bill-of-labour-wo', id)));
          setDeletedLabourIds([]);
        }
        if (deletedServiceIds.length > 0) {
          await Promise.all(deletedServiceIds.map((id) => deleteOne('bill-of-services-wo', id)));
          setDeletedServiceIds([]);
        }
        if (workOrderEditingId) {
          const syncedParts = await saveWorkOrderParts(workOrderEditingId, workOrderParts);
          setWorkOrderParts(syncedParts);
          await Promise.all(
            workOrderEquipment.map((e) =>
              e.id
                ? update('bill-of-equipment-wos', e.id, e)
                : create('bill-of-equipment-wos', { ...e, workOrderId: workOrderEditingId })
            )
          );
          await Promise.all(
            workOrderLabour.map((l) =>
              l.id
                ? update('bill-of-labour-wo', l.id, l)
                : create('bill-of-labour-wo', { ...l, workOrderId: workOrderEditingId })
            )
          );

          await Promise.all(
            workOrderServices.map((s) =>
              s.id
                ? update('bill-of-services-wo', s.id, sanitizeServicePayload(s))
                : create('bill-of-services-wo', {
                  ...sanitizeServicePayload(s),
                  workOrderId: workOrderEditingId,
                })
            )
          );
        }
        handleCloseWorkOrderModal();
      } else {
        const created = await createWorkOrder(payload);
        const createdId = created?.id || created?.data?.id;
        if (!createdId) {
          return;
        }
        didSaveWorkOrder = true;

        if (payload.taskId) {
          const [materials, labour, services, equipment] = await Promise.all([
            getBillOfMaterialsByTaskId(payload.taskId),
            getBillOfLabourByTaskId(payload.taskId),
            getBillOfServicesByTaskId(payload.taskId),
            getBillOfEquipmentByTaskId(payload.taskId),
          ]);

          const mappedItems = mapTaskBillingToWorkOrderItems({
            materials,
            labour,
            services,
            equipment,
            workOrderId: createdId,
          });

          const createdParts = await Promise.all(
            mappedItems.parts.map(async (part) => {
              const createdPart = await create('work-order-parts', {
                ...part,
                workOrderId: createdId,
                cost: Number(part.cost || 0),
              });
              return { ...part, id: createdPart?.id ?? part.id, workOrderId: createdId };
            }),
          );
          const createdLabour = await Promise.all(
            mappedItems.labour.map(async (item) => {
              const createdItem = await create('bill-of-labour-wo', { ...item, workOrderId: createdId });
              return { ...item, id: createdItem?.id ?? item.id, workOrderId: createdId };
            }),
          );
          const createdServices = await Promise.all(
            mappedItems.services.map(async (item) => {
              const createdItem = await create('bill-of-services-wo', {
                ...sanitizeServicePayload(item),
                workOrderId: createdId,
              });
              return { ...item, id: createdItem?.id ?? item.id, workOrderId: createdId };
            }),
          );
          const createdEquipment = await Promise.all(
            mappedItems.equipment.map(async (item) => {
              const createdItem = await create('bill-of-equipment-wos', { ...item, workOrderId: createdId });
              return { ...item, id: createdItem?.id ?? item.id, workOrderId: createdId };
            }),
          );

          setWorkOrderParts(createdParts);
          setWorkOrderLabour(createdLabour);
          setWorkOrderServices(createdServices);
          setWorkOrderEquipment(createdEquipment);
        } else {
          setWorkOrderParts([]);
          setWorkOrderLabour([]);
          setWorkOrderServices([]);
          setWorkOrderEquipment([]);
        }

        const refreshed = await getWorkOrderById(createdId);
        if (refreshed) {
          setNewWorkOrder({
            ...refreshed,
            workorderFiles: Array.isArray(refreshed.workorderFiles) ? refreshed.workorderFiles : [],
            workOrderImage: Array.isArray(refreshed.workOrderImage) ? refreshed.workOrderImage : [],
            completionImages: Array.isArray(refreshed.completionImages) ? refreshed.completionImages : [],
          });
          updateComponentPendingWorkOrder(refreshed);
        }
        setWorkOrderEditingId(createdId);
        setWorkOrderGeneralOnly(false);
      }
    } catch (error) {
      console.error('Error saving work order', error);
    } finally {
      if (didSaveWorkOrder) {
        refreshComponentTable();
      }
      setWorkOrderModalLoading(false);
    }
  }, [
    deletedEquipmentIds,
    deletedLabourIds,
    deletedPartIds,
    deletedServiceIds,
    newWorkOrder,
    workOrderEditingId,
    workOrderEquipment,
    workOrderLabour,
    workOrderParts,
    workOrderServices,
    setWorkOrderErrors,
    duration,
    actualDuration,
    normalizeDurationValue,
    handleCloseWorkOrderModal,
    refreshComponentTable,
    updateComponentPendingWorkOrder,
    workOrderValidationFields,
    sanitizeServicePayload,
  ]);

  const unitName = (component) =>
    component.assetMeter?.meter?.measurementUnits?.unitName || "Days";

  const exportRows = useMemo(() =>
    components.map((component) => [
      assetLabel(component),
      taskName(component),
      component.code?.code,
      component.modifierTask?.code,
      component.frequency ?? "",
      unitName(component),
      component.statutory ? "Yes" : "No",
    ]),
    [components]);

  const exportFileName = taskType === "service" ? "services" : taskType === "compliance" ? "compliance" : "components";

  const handleExportExcel = () => {
    const headers = ["Asset", "Task Name", "Code", "Modifier Code", "Frequency", "UOM", "Statutory"];
    const worksheetData = [headers, ...exportRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      exportFileName.charAt(0).toUpperCase() + exportFileName.slice(1),
    );
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${exportFileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const headers = ["Asset", "Task Name", "Code", "Modifier Code", "Frequency", "UOM", "Statutory"];
    const csvLines = [
      headers.join(","),
      ...exportRows.map((row) =>
        row
          .map((value) => {
            const safe = value ?? "";
            const stringValue = typeof safe === "number" ? String(safe) : safe;
            return `"${String(stringValue).replace(/"/g, '""')}"`;
          })
          .join(","),
      ),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${exportFileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const nextServiceTaskHeader = isServiceLikeTaskType ? "Next Service" : "Next Task";

  const getComplianceStatus = (remaining, frequency, nextOccurrenceDate) => {

    const percentRemain = frequency !== 0 ? remaining / frequency : 0;

    if (percentRemain > 0.2) {
      return { label: "OK", icon: "mdi:check", className: "bg-success-focus text-success px-10" };
    }

    if (percentRemain <= 0.2 && percentRemain >= 0) {
      return { label: "Due Soon", icon: "mdi:warning-outline", className: "bg-warning-focus text-warning px-10" };
    }

    return { label: "Overdue", icon: "mdi:warning-octagon-outline", className: "bg-danger-focus text-danger px-10" };
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex flex-wrap align-items-top gap-2">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-top gap-2 ms-auto me-8">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          {viewMode === "table" && (
            <div className="card-body p-0">
              <div className="card h-100 p-0 rounded-0">
                <div className="card-body p-0">
                  <div
                    className="paginated-table-container scroll"
                    ref={listContainerRef}
                    style={{ height: "calc(100vh - 210px)" }}
                  >
                    <table className="table bordered-table mb-3 rounded-0 w-100">
                      <thead>
                        <tr>
                          <th></th>
                          {renderSortableHeader("Asset", "asset")}
                          {renderSortableHeader("Code", "code")}
                          {renderSortableHeader("Name", "taskname")}
                          <th className={`text-center`}>Asset LTD</th>
                          {!isServiceLikeTaskType && !shouldShowComplianceStatus && <th>Task LTD</th>}
                          {!isServiceLikeTaskType && !shouldShowComplianceStatus && <th>Last Done</th>}
                          {!isServiceLikeTaskType && !shouldShowComplianceStatus && renderSortableHeader("Last Date", "lastDate")}
                          {renderSortableHeader("Next Date", "nextDate")}
                          {shouldShowComplianceStatus && <th>Status</th>}
                          {renderSortableHeader(isServiceLikeTaskType ? "Interval" : "Target", "frequency")}
                          <th>Last Done</th>
                          {renderSortableHeader("Due Δ", "remaining")}
                          {(isServiceLikeTaskType || shouldShowComplianceStatus) && <th>{nextServiceTaskHeader}</th>}
                          <th>Next Work Order</th>
                          <th>Parts Status</th>
                          {(isServiceLikeTaskType && showCostResp) && <th>Cost Reps.</th>}
                          {renderSortableHeader("UOM", "uom")}
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {components.length === 0 && !loading && (
                          <tr>
                            <td colSpan={tableColumnCount} className="text-center py-4">
                              No tasks found
                            </td>
                          </tr>
                        )}
                        {components.map((component) => {
                          const meterEstimatedValue = component?.taskReadingEstimate?.estimatedValue;
                          const numericMeterValue =
                            meterEstimatedValue != null ? Number(meterEstimatedValue) : null;
                          const meterEstimateFlag =
                            Number(component?.taskReadingEstimate?.estimationFlag) === 1;
                          const displayMeterValue =
                            numericMeterValue != null && !Number.isNaN(numericMeterValue)
                              ? Math.round(numericMeterValue)
                              : "";
                          const workOrdersForTask = component.workOrders;
                          const filteredWorkOrders = (workOrdersForTask ?? []).filter(
                            (wo) => ![1, 2].includes(wo.statusId),
                          );
                          const complianceStatus = shouldShowComplianceStatus
                            ? getComplianceStatus(component?.lifeRemaining, component?.frequency, component?.nextOccurrenceDate)
                            : null;
                          const riskOpp =
                            component.componentRisk === 1
                              ? "R"
                              : component.componentOpportunity === 1
                                ? "O"
                                : "";

                          const partsStatus = component?.pendingWorkOrder?.[0]?.partsStatus ?? '';

                          const badgeVariant = partsStatus.toLowerCase().includes('cancelled')
                            ? 'danger'
                            : partsStatus.toLowerCase().includes('received')
                              ? 'success'
                              : partsStatus === 'Not Ordered'
                                ? 'warning'
                                : partsStatus.includes('Ordered') || partsStatus.includes('Approved')
                                  ? 'primary'
                                  : 'secondary';

                          return (
                            <tr key={component.id}>
                              <td>{riskOpp && (
                                <span className={`badge ${riskOpp === "R" ? "bg-danger" : "bg-success"}`}>
                                  {riskOpp}
                                </span>
                              )}</td>
                              <td>{assetLabel(component)}</td>
                              <td className={`text-left`}>
                                {typeof component.code === "string" && component.code
                                  ? component.code
                                  : component.code?.code
                                    ? `${component.code.code} ${component.modifierTask?.code !== undefined
                                      ? component.modifierTask?.code
                                      : ""
                                    }`
                                    : ""}
                              </td>
                              <td>
                                <span className="d-inline-flex align-items-center gap-1">
                                  <span>{taskName(component)}</span>
                                  {String(component?.forecastBasis || "").toLowerCase() === "fixed" && (
                                    <span className="badge bg-primary ms-6">Fixed</span>
                                  )}
                                  {String(component?.schedulingType || "").toLowerCase() === "cycle" && (
                                    <span className="badge bg-primary-400 ms-6">Cycle</span>
                                  )}
                                  {String(component?.schedulingType || "").toLowerCase() === "suppression" && (
                                    <span className="badge bg-primary-900 ms-6">Sup</span>
                                  )}
                                </span>
                              </td>
                              <td className={`text-center`}>
                                {displayMeterValue}
                                {meterEstimateFlag && (
                                  <span
                                    className="badge bg-primary-200 ms-6"
                                    style={{ position: "relative", top: "-1px" }}>
                                    Est
                                  </span>
                                )}
                              </td>
                              {!isServiceLikeTaskType && !shouldShowComplianceStatus && (
                                <td className={`text-center`}>
                                  {Math.round(component?.lifeToDate)}
                                </td>
                              )}
                              {!isServiceLikeTaskType && !shouldShowComplianceStatus && <td className={`text-center`}>
                                {Math.round(component?.lastChangedReading)}
                              </td>}
                              {!isServiceLikeTaskType && !shouldShowComplianceStatus && <td className={`text-end`}>
                                {formatIntDate(component?.lastChangedDate)}
                              </td>}
                              <td className={`text-end`}>
                                {component?.usageOccurrences !== undefined ? formatIntDate(component?.nextOccurrenceDate) : ""}
                              </td>
                              {shouldShowComplianceStatus && (
                                <td>
                                  {complianceStatus ? (
                                    <span
                                      className={`badge ${complianceStatus.className} d-inline-flex align-items-center gap-2`}
                                    >
                                      <Icon icon={complianceStatus.icon} className="text-lg" />
                                      {complianceStatus.label}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              )}
                              <td className={`text-center`}>
                                {Math.round(component.frequency) ?? ""}
                              </td>
                              <td
                                className={component?.calendarBased === 1 ? "text-end" : "text-center"}
                                style={{ width: "85px" }}
                              >
                                {component?.calendarBased === 1
                                  ? formatIntDate(component?.lastDoneDate)
                                  : Math.round(component?.lastDone)}
                              </td>
                              <td
                                className={`text-center ${component.frequency
                                  ? getFocusClass(component.lifeRemaining / component.frequency)
                                  : ""
                                  }`}
                                style={{ width: "85px" }}
                              >
                                {Math.round(component.lifeRemaining) === 0 ? (
                                  <Icon
                                    icon="mdi:exclamation-thick"
                                    className="text-danger text-xl"
                                  />
                                ) : (
                                  Math.round(component.lifeRemaining)
                                )}
                              </td>
                              {(isServiceLikeTaskType || shouldShowComplianceStatus) && <td>
                                <span className="badge bg-secondary">{component.nextServiceTask}</span>
                              </td>}
                              <td>
                                {component?.pendingWorkOrder?.[0]?.workOrderNumber ? (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    onClick={() => handleWorkOrderLinkClick(
                                      component?.pendingWorkOrder?.[0]?.id
                                      ?? component?.pendingWorkOrder?.[0]?.workOrderId,
                                    )}
                                  >
                                    {component?.pendingWorkOrder[0]?.workOrderNumber}
                                  </button>
                                ) :
                                  <button
                                    type="button"
                                    className={`w-32-px h-32-px bg-primary-focus text-primary-main rounded-circle d-inline-flex align-items-center justify-content-center`}
                                    disabled={component?.pendingWorkOrder?.[0]?.workOrderNumber}
                                    onClick={() => handleOpenAddWorkOrder(component)}
                                  >
                                    <Icon icon="mdi:plus" className="icon text-xl line-height-1" />
                                  </button>}
                              </td>
                              <td>
                                <span className={`badge bg-${badgeVariant}`}>{component?.pendingWorkOrder[0]?.partsStatus}</span>
                              </td>
                              {(isServiceLikeTaskType && showCostResp) &&
                                <td>
                                  {component?.costResponsibility?.name}
                                </td>
                              }
                              <td>{unitName(component)}</td>
                              <td className="text-end">
                                <div className="d-inline-flex align-items-center justify-content-end gap-2">
                                  <button
                                    type="button"
                                    className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                    onClick={() => {
                                      setSelectedTaskId(component.id);
                                      setShowTaskModal(true);
                                    }}
                                  >
                                    <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                                  </button>
                                  <button
                                    type="button"
                                    className="w-32-px h-32-px bg-alert-focus text-alert-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                    onClick={() => handleReviewComponent(component)}
                                  >
                                    <Icon icon="mdi:cog-outline" className="icon text-xl line-height-1" />
                                  </button>
                                  {!workOrdersEnabled && (<button
                                    type="button"
                                    className="w-32-px h-32-px bg-primary-focus text-primary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                    onClick={() => {
                                      handleReplaceComponent(component)
                                    }}
                                  >
                                    <Icon icon="mdi:settings-refresh-outline" className="icon text-xl line-height-1" />
                                  </button>)}
                                  <div className="dropdown">
                                    <button
                                      className="btn btn-link text-decoration-none text-body"
                                      type="button"
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                    >
                                      <Icon icon="mdi:dots-vertical" className="text-xl" />
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end shadow">
                                      <li>
                                        <button
                                          className="dropdown-item d-flex align-items-center gap-2"
                                          disabled={component?.pendingWorkOrder?.[0]?.workOrderNumber}
                                          onClick={() => handleOpenAddWorkOrder(component)}
                                        >
                                          <Icon icon="mdi:plus" />
                                          Add Work Order
                                        </button>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {loading && (
                          <tr>
                            <td colSpan={tableColumnCount} className="text-center py-4">
                              <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
          {viewMode === "calendar" && (
            <div className="card h-100 p-0 rounded-0 mt-24">
              <div className="card-body p-0">
                {calendarLoading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "calc(100vh - 210px)" }}>
                    <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
                  </div>
                ) : (
                  <Calendar
                    events={calendarEvents}
                    editable={false}
                    selectable={false}
                    onEventClick={(event) => {
                      if (!event?.taskId) return;
                      if (event?.workOrderNumber && event?.workOrderId) {
                        handleOpenEditWorkOrder(event.workOrderId);
                        return;
                      }
                      setSelectedTaskId(event.taskId);
                      setShowTaskModal(true);
                    }}
                    onEventDoubleClick={(event) => {
                      if (event?.workOrderNumber && event?.workOrderId) {
                        handleOpenEditWorkOrder(event.workOrderId);
                        return;
                      }
                      if (!event?.taskId) return;
                      setSelectedTaskId(event.taskId);
                      setShowTaskModal(true);
                    }}
                    eventClassNameResolver={(event) => (
                      event?.hasWorkOrder
                        ? ["bg-primary-focus", "text-primary"]
                        : ["bg-secondary-focus", "text-secondary"]
                    )}
                    eventContentRenderer={(eventInfo) => {
                      const event = eventInfo.event.extendedProps;
                      return (
                        <div className="px-4 py-2 text-truncate">
                          {event?.workOrderNumber && <div className="fw-semibold">#{event.workOrderNumber}</div>}
                          {event?.assetNumber && <div>{event.assetNumber}</div>}
                          {event?.componentLabel && <div>{event.componentLabel}</div>}
                        </div>
                      );
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <TaskEditModal
          taskId={selectedTaskId}
          show={showTaskModal}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTaskId(null);
          }}
          onSaved={() => {
            setComponents([]);
            setPage(1);
            setHasMore(true);
            setReloadKey((prev) => prev + 1);
            setTaskComments({});
            commentFetchInProgress.current.clear();
          }}
        />
        <ReplaceComponentWorkOrderModal
          show={showReplaceModal}
          component={selectedComponent}
          mode={replaceMode}
          existingWorkOrder={existingWorkOrder}
          onClose={() => {
            setShowReplaceModal(false);
            setSelectedComponent(null);
            setExistingWorkOrder(null);
            setReplaceMode("create");
          }}
          onSaved={() => {
            setComponents([]);
            setPage(1);
            setHasMore(true);
            setReloadKey((prev) => prev + 1);
            setExistingWorkOrder(null);
            setReplaceMode("create");
            setTaskComments({});
            commentFetchInProgress.current.clear();
          }}
        />
        <ComponentWorkOrderSelectionModal
          show={showWorkOrderSelectionModal}
          workOrders={matchingWorkOrders}
          loading={checkingExistingWorkOrders}
          onClose={() => {
            if (checkingExistingWorkOrders) return;
            setShowWorkOrderSelectionModal(false);
            setMatchingWorkOrders([]);
            setExistingWorkOrder(null);
            setReplaceMode("create");
            setSelectedComponent(null);
          }}
          onAddNew={() => {
            if (checkingExistingWorkOrders) return;
            setShowWorkOrderSelectionModal(false);
            setMatchingWorkOrders([]);
            setExistingWorkOrder(null);
            setReplaceMode("create");
            setShowReplaceModal(true);
          }}
          onComplete={(workOrder) => {
            if (checkingExistingWorkOrders) return;
            handleCompleteExistingWorkOrder(workOrder);
          }}
        />
        <ComponentWorkOrdersModal
          show={showReviewModal}
          component={reviewComponent}
          taskOccurrences={taskOccurrences}
          workOrderStatuses={workOrderStatuses}
          comments={reviewComponent?.id ? taskComments[reviewComponent.id] || [] : []}
          commentsLoading={reviewCommentsLoading}
          onCommentCreate={async (commentText) => {
            if (!reviewComponent?.id) return;
            await handleCreateTaskComment(reviewComponent.id, commentText);
          }}
          onCommentUpdate={async (commentId, updatedText) => {
            if (!reviewComponent?.id) return;
            await handleUpdateTaskComment(reviewComponent.id, commentId, updatedText);
          }}
          onCommentDelete={async (commentId) => {
            if (!reviewComponent?.id) return;
            await handleDeleteTaskComment(reviewComponent.id, commentId);
          }}
          canCreateComments={canCreateTaskComments}
          canEditComments={canEditTaskComments}
          canDeleteComments={canDeleteTaskComments}
          formatDate={formatIntDate}
          onWorkOrderClick={handleWorkOrderLinkClick}
          onClose={(shouldReload) => {
            setShowReviewModal(false);
            setReviewComponent(null);
            setTaskOccurrences([]);
            if (shouldReload) {
              setComponents([]);
              setPage(1);
              setHasMore(true);
              setReloadKey((prev) => prev + 1);
            }
          }}
        />
        <WorkOrderEditModal
          showModal={showAddWorkOrderModal}
          editingId={workOrderEditingId}
          newWorkOrder={newWorkOrder}
          modalLoading={workOrderModalLoading}
          generalTabHasError={Object.keys(workOrderErrors).length > 0}
          showGeneralOnly={workOrderGeneralOnly}
          showCreateEventToggle={false}
          onClose={handleCloseWorkOrderModal}
          onCreateEventToggle={() => { }}
          workOrderGeneralFields={workOrderGeneralFields}
          renderGeneralFields={renderWorkOrderField}
          duration={duration}
          onDurationChange={handleDurationChange}
          combinedErrors={workOrderErrors}
          onDurationBlur={handleDurationBlur}
          actualDuration={actualDuration}
          onActualDurationChange={handleActualDurationChange}
          onActualDurationBlur={handleActualDurationBlur}
          workOrderCompleteFields={workOrderCompleteFields}
          renderCompleteFields={renderCompleteFields}
          renderProgressFields={renderProgressFields}
          partsCostTotal={0}
          actualPartsCost={actualPartsCost}
          onActualPartsCostChange={setActualPartsCost}
          labourCostTotal={0}
          actualLabourCost={actualLabourCost}
          onActualLabourCostChange={setActualLabourCost}
          servicesCostTotal={0}
          actualMiscCost={actualMiscCost}
          onActualMiscCostChange={setActualMiscCost}
          labourHoursTotal={0}
          actualLabourHours={actualLabourHours}
          onActualLabourHoursChange={setActualLabourHours}
          workOrderSchedule={null}
          scheduleFields={[]}
          renderScheduleField={() => null}
          workOrderParts={workOrderParts}
          setWorkOrderParts={setWorkOrderParts}
          deletedPartIds={deletedPartIds}
          setDeletedPartIds={setDeletedPartIds}
          userProfile={userProfile}
          expenseOptions={expenseOptions}
          defaultPartsExpenseCode={defaultPartsExpenseCode}
          workOrderLabour={workOrderLabour}
          setWorkOrderLabour={setWorkOrderLabour}
          deletedLabourIds={deletedLabourIds}
          setDeletedLabourIds={setDeletedLabourIds}
          tradeOptions={tradeOptions}
          defaultLabourExpenseCode={defaultLabourExpenseCode}
          setLabourForm={setLabourForm}
          setEditingLabourIndex={setEditingLabourIndex}
          setLabourErrors={setLabourErrors}
          setShowLabourModal={setShowLabourModal}
          isWorkOrderLocked={isWorkOrderLocked}
          workOrderServices={workOrderServices}
          setWorkOrderServices={setWorkOrderServices}
          setDeletedServiceIds={setDeletedServiceIds}
          defaultServiceExpenseCode={defaultServiceExpenseCode}
          serviceOptions={serviceOptions}
          workOrderEquipment={workOrderEquipment}
          setWorkOrderEquipment={setWorkOrderEquipment}
          deletedEquipmentIds={deletedEquipmentIds}
          setDeletedEquipmentIds={setDeletedEquipmentIds}
          setEditingEquipmentIndex={setEditingEquipmentIndex}
          setEquipmentForm={setEquipmentForm}
          setEquipmentErrors={setEquipmentErrors}
          setShowEquipmentModal={setShowEquipmentModal}
          workOrderForms={workOrderForms}
          onViewForm={handleViewForm}
          onPrintForm={handlePrintForm}
          onDeleteForm={() => { }}
          onOpenAddFormModal={openAddFormModal}
          workOrderDocumentFields={workOrderDocumentFields}
          renderDocumentFields={renderDocumentField}
          onFileDelete={handleWorkOrderFileDelete}
          handleSave={handleSaveWorkOrder}
          showEquipmentModal={showEquipmentModal}
          editingEquipmentIndex={editingEquipmentIndex}
          equipmentForm={equipmentForm}
          equipmentErrors={equipmentErrors}
          clearEquipmentFieldError={clearEquipmentFieldError}
          onCloseEquipmentModal={() => setShowEquipmentModal(false)}
          onSaveEquipment={handleSaveEquipment}
          showLabourModal={showLabourModal}
          editingLabourIndex={editingLabourIndex}
          labourForm={labourForm}
          labourErrors={labourErrors}
          clearLabourFieldError={clearLabourFieldError}
          onCloseLabourModal={() => setShowLabourModal(false)}
          onSaveLabour={handleSaveLabour}
          showFormModal={showFormModal}
          selectedFormId={selectedFormId}
          formOptions={formOptions}
          onSelectedFormChange={setSelectedFormId}
          onCloseFormModal={closeFormModal}
          onSaveFormSelection={handleSaveFormSelection}
        />
        {showWorkOrderDetailsModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {workOrderDetailsLoading ? ("Work Order Details") : (`${workOrderDetails.workOrderNumber} - ${workOrderDetails.workOrderTitle}`)}
                  </h5>
                  <button type="button" className="btn-close" onClick={handleCloseWorkOrderDetails} />
                </div>
                <div className="modal-body">
                  {workOrderDetailsLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-4">
                      <div className="spinner-border" role="status" aria-label="Loading work order details" />
                    </div>
                  ) : (
                    <WorkOrderDetails
                      workOrder={workOrderDetails || selectedWorkOrder}
                      parts={workOrderParts}
                      labour={workOrderLabour}
                      services={workOrderServices}
                      equipment={workOrderEquipment}
                      forms={workOrderForms}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showFormView && (
          <FormViewModal
            workOrderFormId={viewWorkOrderFormId}
            onClose={() => {
              setShowFormView(false);
              setViewWorkOrderFormId(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default ComponentManagementLayer;